import {
  HOST_CLIENT_KEYFRAME_MIN_INTERVAL_MS,
  capabilityForEngine,
  decideKeyframe,
  type GuestBrowserEngine,
  type GuestLinkDebugSnapshot,
  type KeyframeReason,
} from '@playcastdotio/PeerCore';

export type KeyframeRequestSource = 'client' | 'host';

export type KeyframeGateDeps = {
  /** The guest's engine once its clientStats have named it. */
  engineFor: (peerId: string) => GuestBrowserEngine | undefined;
  /** The guest's latest jitter-buffer / glass-to-glass reading, for the correlation record. */
  guestLinkFor: (peerId: string) => GuestLinkDebugSnapshot | undefined;
  /** Where `[Keyframe]` lines go. Default console.log; the owner routes them to the debug NDJSON sink too. */
  log?: (tag: string, fields: Record<string, unknown>) => void;
  nowMs?: () => number;
};

export type KeyframeAdmission = { admit: true } | { admit: false; why: 'throttled' | 'withheld' };

/**
 * KeyframeGate - the one place every host IDR passes through before it reaches the encoder.
 *
 * Two jobs. Policy: client requests are rate-limited (the guest retries every second until
 * its first decoded frame) and cosmetic keyframes are withheld from engines whose jitter
 * buffer they inflate (`decideKeyframe`). Record: every request that goes out and every one
 * withheld is logged as a `[Keyframe]` line beside the guest's jitter-buffer target at that
 * moment, and the time since the last keyframe is exposed so the `[JitterBuffer]` tick can
 * carry it - a target step-up a few seconds after a keyframe is the signature this exists to
 * catch. Pure bookkeeping: the setParameters call stays with the connection owner.
 */
export class KeyframeGate {
  private readonly nowMs: () => number;
  private readonly lastClientAtByPeer = new Map<string, number>();
  private readonly lastKeyframeAtByPeer = new Map<string, number>();
  private readonly withheldCountByPeer = new Map<string, number>();
  private readonly log: (tag: string, fields: Record<string, unknown>) => void;

  constructor(private readonly deps: KeyframeGateDeps) {
    this.nowMs = deps.nowMs ?? (() => Date.now());
    this.log = deps.log ?? ((tag, fields) => console.log(tag, fields));
  }

  /**
   * Apply client rate limits and the engine's keyframe policy. framesDropped is the
   * burst's dropped-frame count and does not admit a cosmetic keyframe. Android
   * Chrome withholds loss-burst-end along with the other cosmetic reasons.
   * Admission does not send a frame; unthrottled client attempts update the
   * throttle timestamp.
   */
  public admit(
    peerId: string,
    source: KeyframeRequestSource,
    reason: KeyframeReason,
    framesDropped = 0,
  ): KeyframeAdmission {
    const now = this.nowMs();
    if (source === 'client') {
      const last = this.lastClientAtByPeer.get(peerId) ?? 0;
      if (now - last < HOST_CLIENT_KEYFRAME_MIN_INTERVAL_MS) {
        this.log('[Keyframe] throttled', { peerId, reason, sinceLastClientMs: now - last });
        return { admit: false, why: 'throttled' };
      }
      this.lastClientAtByPeer.set(peerId, now);
    }

    const engine = this.deps.engineFor(peerId);
    const decision = decideKeyframe(
      reason,
      engine ? capabilityForEngine(engine) : undefined,
      framesDropped,
    );
    if (!decision.send) {
      const withheld = (this.withheldCountByPeer.get(peerId) ?? 0) + 1;
      this.withheldCountByPeer.set(peerId, withheld);
      this.log('[Keyframe] withheld', { peerId, reason, engine, withheldCount: withheld, ...this.linkFields(peerId), why: decision.withheld });
      return { admit: false, why: 'withheld' };
    }
    return { admit: true };
  }

  /** Record that the encoder accepted a keyframe request. */
  public noteRequested(peerId: string, source: KeyframeRequestSource, reason: KeyframeReason, idrCount: number): void {
    this.lastKeyframeAtByPeer.set(peerId, this.nowMs());
    this.log('[Keyframe] requested', { peerId, reason, source, engine: this.deps.engineFor(peerId), idrCount, ...this.linkFields(peerId) });
  }

  /** Milliseconds since the last accepted keyframe, or undefined before the first. */
  public sinceKeyframeMs(peerId: string): number | undefined {
    const at = this.lastKeyframeAtByPeer.get(peerId);
    return at === undefined ? undefined : this.nowMs() - at;
  }

  public withheldCount(peerId: string): number {
    return this.withheldCountByPeer.get(peerId) ?? 0;
  }

  public clearPeer(peerId: string): void {
    this.lastClientAtByPeer.delete(peerId);
    this.lastKeyframeAtByPeer.delete(peerId);
    this.withheldCountByPeer.delete(peerId);
  }

  private linkFields(peerId: string): Record<string, number | null> {
    const link = this.deps.guestLinkFor(peerId);
    return {
      jbDelayMs: link?.jitterBufferMs ?? null,
      jbTargetMs: link?.jitterBufferTargetMs ?? null,
      g2gMs: link?.glassToGlassMs ?? null,
    };
  }
}
